import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  changePassword,
  login,
  resetPassword,
  signup,
  updateProfile,
  sendOtp,
  verifyOtp,
  GetUserByPhoneNumber,
} from "../API/authApiservice";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../store/useAuth";
import useAppointmentStore from "../store/useAppointmentStore";
import showToast from "../utils/toastHelper";

export const useSignup = () => {  const navigate = useNavigate();
  const location = useLocation();
  const { setIsRegistred, setIsAuthenticated, setIsLoading, setUser } = useAuth();
  const { setSelectedFacility } = useAppointmentStore();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const from = location.state?.from?.pathname || "/";
  const fromFacilityQR = location.state?.fromQR === true || localStorage.getItem("fromQR") === "true";
  const fromSpecializationQR = location.state?.fromSpecializationQR === true || localStorage.getItem("fromSpecializationQR") === "true";
  const qrRedirectTarget = fromFacilityQR 
    ? (location.state?.qrRedirectTarget || sessionStorage.getItem("qrRedirectTarget") || "/qr-doctors")
    : (fromSpecializationQR ? "/doctors" : "/");
  
  const { mutateAsync, isPending } = useMutation({
    mutationKey: ["signup"],
    mutationFn: signup,    onSuccess: (data) => {
      if (data?.successful === true && data?.item?.accessToken) {

        localStorage.setItem("token", data.item.accessToken);
        if (data.item.idToken) {
          localStorage.setItem("idToken", data.item.idToken);
        }
        const signupRefresh = (data as { item?: { refreshToken?: string } })?.item?.refreshToken;
        if (signupRefresh) {
          localStorage.setItem("refreshToken", signupRefresh);
        }
        
   
        setSelectedFacility(data.item.facilityId);
        setIsAuthenticated(true);
        setIsLoading(false);
        setIsRegistred(true);
        

        setUser({
          id: data.item.id.toString(),
          firstName: data.item.firstName,
          middleName: data.item.middleName,
          lastName: data.item.lastName,
          email: data.item.email,
          status: data.item.status,
          phoneNumber: data.item.phoneNumber,
          facilityId: data.item.facilityId,
        });
        
 
        localStorage.setItem("userLoggedIn", "true");
        localStorage.setItem("userRegistered", "true");
        localStorage.setItem("appInitialized", "true");
        localStorage.setItem("phoneNumber", data.item.phoneNumber);
        
      
        setErrorMessage(null);
        
        queryClient.invalidateQueries({ queryKey: ["signup"] });      
        
        showToast("Account created successfully! You are now logged in.", "success");
            const intendedPath = localStorage.getItem("intendedPath") || sessionStorage.getItem("originalIntendedPath");
        if (intendedPath && intendedPath !== "/") {
          localStorage.removeItem("intendedPath");
          sessionStorage.removeItem("originalIntendedPath");
          window.location.href = intendedPath;


        } else if (fromFacilityQR || fromSpecializationQR) {
    
          
          if (fromFacilityQR && qrRedirectTarget) {
            window.location.href = qrRedirectTarget;
          } else if (fromSpecializationQR) {
            window.location.href = "/doctors";
          } else {
     
            navigate(from, { replace: true });
          }
        } else {
          navigate(from, { replace: true });
        }
        
      } else if (data?.errors?.some(error => 
        error.messages?.some(msg => msg.includes("phone number must be unique")))) {
    
        showToast("Account already exists with this phone number. Redirecting to sign in.", "error");
        

        const originalIntendedPath = sessionStorage.getItem("originalIntendedPath");
        if (originalIntendedPath) {
          localStorage.setItem("intendedPath", originalIntendedPath);
        }
        
       
        setTimeout(() => {
          navigate("/auth?mode=signin", {
            state: {
              autoFillPhone: true,
              verifiedPhone: location.state?.verifiedPhone || sessionStorage.getItem("lastVerifiedPhone"),
              fromQR: fromFacilityQR,
              fromSpecializationQR: fromSpecializationQR,
              qrRedirectTarget: qrRedirectTarget
            },
            replace: true,
          });
        }, 1000);
        
      } else if (data?.errors && data?.errors?.length > 0) {
        const message = data.errors[0]?.messages?.[0] || "Failed to create account";
        setErrorMessage(message);
      } else {
        setErrorMessage("Failed to create account. Please try again.");
      }
    },
    onError: (error: Error) => {
      setErrorMessage(error?.message || "Something went wrong");
    }
  });

  return {
    signup: mutateAsync,
    signupPending: isPending,
    signupError: errorMessage
  };
};
export const useLogin = () => {
  const navigate = useNavigate();
  const { setIsAuthenticated, setIsLoading, setUser } = useAuth();
  const { setSelectedFacility } = useAppointmentStore();
  const queryCleint = useQueryClient();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  
  const parseLockoutTime = (message: string): number | null => {
    const timeMatch = message.match(/(\d+)\s*(minute|min|hour|hr|second|sec)/i);
    if (timeMatch) {
      const value = parseInt(timeMatch[1], 10);
      const unit = timeMatch[2].toLowerCase();
      if (unit.includes('min')) {
        return value * 60 * 1000;
      } else if (unit.includes('hour') || unit.includes('hr')) {
        return value * 60 * 60 * 1000;
      } else if (unit.includes('sec')) {
        return value * 1000;
      }
    }
    return null;
  };
  
  const getLockoutExpirationTime = (message: string): number => {
    const lockoutDuration = parseLockoutTime(message);
    if (lockoutDuration !== null) {
      if (lockoutDuration === 0) {
        return Date.now() + 5000;
      }
      return Date.now() + lockoutDuration;
    }
    return Date.now() + (10 * 60 * 1000);
  };
  
  useEffect(() => {
    const storedLockout = sessionStorage.getItem("accountLocked");
    const storedExpiration = sessionStorage.getItem("accountLockoutExpiration");
    
    if (storedLockout === "true" && storedExpiration) {
      const expirationTime = parseInt(storedExpiration, 10);
      const now = Date.now();
      
      if (now < expirationTime) {
        const storedMessage = sessionStorage.getItem("accountLockoutMessage");
        setIsAccountLocked(true);
        setErrorMessage(storedMessage || "Account is locked. Please try again later.");
      } else {
        sessionStorage.removeItem("accountLocked");
        sessionStorage.removeItem("accountLockoutMessage");
        sessionStorage.removeItem("accountLockoutExpiration");
        setIsAccountLocked(false);
        setErrorMessage(null);
      }
    }
  }, []);
  
  useEffect(() => {
    if (!isAccountLocked) return;
    
    const checkExpiration = () => {
      const storedExpiration = sessionStorage.getItem("accountLockoutExpiration");
      if (storedExpiration) {
        const expirationTime = parseInt(storedExpiration, 10);
        const now = Date.now();
        
        if (now >= expirationTime) {
          sessionStorage.removeItem("accountLocked");
          sessionStorage.removeItem("accountLockoutMessage");
          sessionStorage.removeItem("accountLockoutExpiration");
          setIsAccountLocked(false);
          setErrorMessage(null);
        }
      }
    };
    
    const interval = setInterval(checkExpiration, 1000);
    return () => clearInterval(interval);
  }, [isAccountLocked]);
  
  const { mutateAsync, isPending } = useMutation({
    mutationKey: ["login"],
    mutationFn: login,
    onSuccess: (data) => {
      if (data?.accessToken) {
        sessionStorage.removeItem("accountLocked");
        sessionStorage.removeItem("accountLockoutMessage");
        sessionStorage.removeItem("accountLockoutExpiration");
        localStorage.setItem("token", data.accessToken);
        if (data?.idToken) {
          localStorage.setItem("idToken", data.idToken);
        }
        if (data?.refreshToken) {
          localStorage.setItem("refreshToken", data.refreshToken);
        }
        setSelectedFacility(data?.user?.facilityId);
        setIsAuthenticated(true);
        setIsLoading(false);
        
        localStorage.setItem("appInitialized", "true");
        localStorage.setItem("userLoggedIn", "true");
      
        setUser({
          ...data.user,
          status: data.user.status,
        });
        queryCleint.invalidateQueries({ queryKey: ["login"] });
        
        setErrorMessage(null);
        setIsAccountLocked(false);
 
        const intendedPath = localStorage.getItem("intendedPath") || sessionStorage.getItem("originalIntendedPath");
        if (intendedPath && intendedPath !== "/") {
          localStorage.removeItem("intendedPath");
          sessionStorage.removeItem("originalIntendedPath");
          window.location.href = intendedPath;
        } else {
          navigate(from, { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const err = error as Error & { isAccountLocked?: boolean };
      const message = err?.message || "Invalid Phone Number or Password";
      const isLocked = err?.isAccountLocked === true || message.toLowerCase().includes("account is locked");
      setErrorMessage(message);
      setIsAccountLocked(isLocked);
      if (isLocked) {
        const expirationTime = getLockoutExpirationTime(message);
        sessionStorage.setItem("accountLocked", "true");
        sessionStorage.setItem("accountLockoutMessage", message);
        sessionStorage.setItem("accountLockoutExpiration", expirationTime.toString());
      } else {
        sessionStorage.removeItem("accountLocked");
        sessionStorage.removeItem("accountLockoutMessage");
        sessionStorage.removeItem("accountLockoutExpiration");
      }
    }
  });
  
  return {
    login: mutateAsync,
    loginPending: isPending,
    loginError: errorMessage,
    isAccountLocked,
    previousRoute: from,
  };
};
export const useUpdateProfile = () => {

  const queryCleint = useQueryClient();
  const { mutateAsync, isPending, isError } = useMutation({
    mutationKey: ["updateProfile"],
    mutationFn: updateProfile,
    onSuccess: (data) => {
      if (data?.successful === true) {
    showToast("Profile updated successfully", "success");
        queryCleint.invalidateQueries({ queryKey: ["updateProfile"] });
      }
    },
  });
  return { updateProfile: mutateAsync, isPending, isError };
};
export const useChangePassword = () => {
const navigate=useNavigate()
  const { mutateAsync, isPending, isError } = useMutation({
    mutationKey: ["changePassword"],
    mutationFn: changePassword,
    onSuccess: (data) => {
      if (data?.successful) {
        showToast("Password changed successfully", "success");
        navigate("/")
  
      }
      else if(data?.errors){
        const errorMessage = data.errors
        .map(err => err.messages.join(', '))
        .join('; ');
      showToast(errorMessage, "error");
    }

      
    },
  });
  return { changePassword: mutateAsync, changingPassword:isPending, chnagePasswordError:isError };
};
export const useResetPassword = () => {
  const { mutateAsync, isPending, isError } = useMutation({
    mutationFn: resetPassword,
    mutationKey: ["resetPassword"],
    onSuccess: (data) => {
      if (data?.successful) {
        window.location.href = "/auth?mode=signin";
      }
    },
  });
  return { resetPassword: mutateAsync, isPending, isError };
};
export const useSendOTP = () => {
  const { mutateAsync, isPending, isError } = useMutation({
    mutationFn: sendOtp,
    mutationKey: ["sendOtp"],
  });
  return { sendOtp: mutateAsync, sendingOtp: isPending, isError };
};
export const useVerifyOTP = () => {
  const { mutateAsync, isPending, isError } = useMutation({
    mutationFn: (data: {
      phoneNumber: string;
      otpCode: string;
      verificationId: string;
    }) => verifyOtp(data.phoneNumber, data.otpCode, data.verificationId),
    mutationKey: ["verifyOtp"],
  });
  return { verifyOtp: mutateAsync, isPending, isError };
};
export const useGetUserByPhoneNumber = () => {
  const { mutateAsync, isPending, isError } = useMutation({
    mutationFn: GetUserByPhoneNumber,
    mutationKey: ["getUserByPhoneNumber"],
  });
  return { getUserByPhoneNumber: mutateAsync, isPending, isError };
}